import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';

import { SoundsWritingErrors } from '../../../../constants/errors.ts';
import { selectCurrentWritingPath } from '../../../../selectors/current-writing-path.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';
import { disableDrawing, resetUserWritingPath } from '../../../../slices/sounds-writing/sounds-writing.slice.ts';
import { WritingPathWithSubPathsAndLetter } from '../../../../types/svg.types.ts';

import { getLetterDrawingUtil } from '../../../letter-drawing.utils.ts';
import { getSvgUtil } from '../../../svg.utils.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Base handler for writing-related steps
 * Provides shared functionality for both app and user writing handlers
 */
export abstract class BaseWritingStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  constructor(
    protected readonly svgUtil = getSvgUtil(),
    protected readonly letterDrawingUtil = getLetterDrawingUtil()
  ) {
    super();
  }

  protected abstract getHandlerName(): string;

  protected async beforeWritingSetup(): Promise<void> {
    // Default implementation does nothing
    // Derived classes can override to add their own setup logic
  }
  protected abstract getNextStateAfterReadyToStart(): Promise<AppStateEnum | null>;

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);

    // Allow derived classes to perform setup
    await this.beforeWritingSetup();

    // Clear writing area - common for all writing handlers
    await this.clearWritingArea();

    // Speak instructions - common for all writing handlers
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Get the next state from the derived class
    const nextState = await this.getNextStateAfterReadyToStart();

    // Transition to the next state
    const dispatch = await this.reduxStoreUtil.getDispatch();
    if (nextState) {
      await dispatch(updateAppStateThunk({ state: nextState, blockAnimation: true })).unwrap();
      return;
    }
    dispatch(setBallIsVisible(true));
  }

  protected async clearWritingArea(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Reset path and hide ball
    dispatch(setBallIsVisible(false));
    dispatch(resetUserWritingPath());
  }

  protected async prepareWritingPath(): Promise<WritingPathWithSubPathsAndLetter | null> {
    const state = await this.reduxStoreUtil.getRootState();

    // Get reference writing path
    const writingPath = selectCurrentWritingPath(state);
    if (!writingPath) {
      this.handleError(new Error(SoundsWritingErrors.INVALID_WRITING_PATH));
      return null;
    }

    // Create subpaths
    const subPaths = this.svgUtil.getSubPaths(writingPath.path);
    const writingPathWithSubPaths = {
      path: writingPath.path,
      subPaths,
      dropsBelowLine: writingPath.dropsBelowLine,
      letter: writingPath.letter,
    };

    return writingPathWithSubPaths;
  }

  protected async animateLetterDrawing(): Promise<boolean> {
    // Prepare path data
    const writingPathWithSubPaths = await this.prepareWritingPath();
    if (!writingPathWithSubPaths) {
      return false;
    }

    // Clear canvas
    await this.letterDrawingUtil.clearDrawing(writingPathWithSubPaths);

    // Animate letter
    await this.letterDrawingUtil.drawLetter(writingPathWithSubPaths);

    return true;
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    await this.handleReadyForNextStep();
  }

  protected override async onAborting(): Promise<void> {
    await super.onAborting();
    console.log(`${this.getHandlerName()} onAborting`);

    // Clean up drawing state
    const dispatch = await this.reduxStoreUtil.getDispatch();
    dispatch(resetUserWritingPath());
    dispatch(disableDrawing());
  }
}
